import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';


import { Collaborators } from 'stores/collaborators_store';
import useStores from 'hooks/useStores';

export type CollaboratorsContextType = {
    snooze: (identity: string, collab: string) => Promise<void>;
    leave: (identity: string, collab: string) => Promise<void>;
    activate: (identity: string, collab: string) => Promise<void>;
    revoke: (identity: string, collab: string, owner_identity: string) => Promise<void>;
    remove: (identity: string, collab: string, owner_identity: string) => Promise<void>;
    fetchIdentityCollaborators: (identity: string, variant: string) => Promise<void>;
    fetchIdentityCollaboratorsByManager: (manager_identity: string, assistant_identity: string, variant: string) => Promise<void>;

    getIdentityCollaborators: (identity: string) => Collaborators[];
    myCollaborators: Collaborators[];
    activeCollaborators: Collaborators[];
    snoozedCollaborators: Collaborators[];
    archivedCollaborators: Collaborators[];
    fetchListForCollab: (identity: string, collab: string) => Promise<Collaborators[]|null>;
    getCollaborators: (collabKey: string) => Collaborators[];
}

export const CollaboratorsContext = React.createContext<CollaboratorsContextType>({
    myCollaborators: [] as Collaborators[],
    activeCollaborators: [] as Collaborators[],
    snoozedCollaborators: [] as Collaborators[],
    archivedCollaborators: [] as Collaborators[],
    fetchListForCollab: () => { throw "Not implemented" },
    getCollaborators: () => { throw "Not implemented" },
    fetchIdentityCollaborators: () => { throw "Not implemented" },
    fetchIdentityCollaboratorsByManager: () => { throw "Not implemented" },
    getIdentityCollaborators: () => { throw "Not implemented" },
    snooze: () => { throw "Not implemented" },
    leave: () => { throw "Not implemented" },
    activate: () => { throw "Not implemented" },
    revoke: () => { throw "Not implemented" },
    remove: () => { throw "Not implemented" }
});


export const CollaboratorsProvider = observer(({ children }: { children: React.ReactNode }) => {
    //    const [currentCollab, setCurrentCollabState] = useState<Collab | null>(null);
    const { userCollaboratorsStore, collaboratorsStore } = useStores();

    const [myCollaborators, setMyCollaborators] = useState([] as Collaborators[]);
    const [activeCollaborators, setActiveCollaborators] = useState([] as Collaborators[]);
    const [snoozedCollaborators, setSnoozedCollaborators] = useState([] as Collaborators[]);
    const [archivedCollaborators, setArchivedCollaborators] = useState([] as Collaborators[]);
    const [revokedCollaborators, setRevokedCollaborators] = useState([] as Collaborators[]);
    const [removedCollaborators, setRemovedCollaborators] = useState([] as Collaborators[]);
    // const { currentIdentity } = useUserIdentity();

    function separateCollaborators(collaborators: Collaborators[]) {
        setActiveCollaborators(collaborators.filter((collab) => {
            return collab.status === 'active';
        }));
        setSnoozedCollaborators(collaborators.filter((collab) => {
            return collab.status === 'snoozed';
        }));
        setArchivedCollaborators(collaborators.filter((collab) => {
            return collab.status === 'left';
        }));
        setRevokedCollaborators(collaborators.filter((collab) => {
            return collab.status === 'revoked';
        }));
        setRemovedCollaborators(collaborators.filter((collab) => {
            return collab.status === 'removed';
        }));
    }

    useEffect(() => {
        if (userCollaboratorsStore.items.length > 0) {
            separateCollaborators(userCollaboratorsStore.items);
        }
    }, [userCollaboratorsStore.items.length]);

    const fetchIdentityCollaborators = async (identity: string, variant: string) => {
        //        console.log("fetching identity collaborators", identity);
        return userCollaboratorsStore.fetchIdentityCollaborators(identity, variant)
            .then(() => {
                let itms = [...userCollaboratorsStore.items];
                itms.sort((a, b) => {
                    return a.date_updated > b.date_updated ? -1 : 1
                })
                setMyCollaborators(itms);
                separateCollaborators(userCollaboratorsStore.items);
            });
    }

    const fetchIdentityCollaboratorsByManager = async (
        manager_identity: string,
        assistant_identity: string,
        variant="collab") => {
            return collaboratorsStore.fetchIdentityCollaboratorsByManager(
                manager_identity,
                assistant_identity,
                variant,
            );
        };


    const snooze = async (identity: string, collab: string) => {
        userCollaboratorsStore.take_action(identity, collab, 'snooze').
        then(() => {
            separateCollaborators(userCollaboratorsStore.items);
        });
    };

    const leave = async (identity: string, collab: string) => {
        userCollaboratorsStore.take_action(identity, collab, 'leave').
        then(() => {
            separateCollaborators(userCollaboratorsStore.items);
        });
    };

    const activate = async (identity: string, collab: string) => {
        userCollaboratorsStore.take_action(identity, collab, 'activate').
        then(() => {
            separateCollaborators(userCollaboratorsStore.items);
        });
    };


    const revoke = async (identity: string, collab: string, owner_identity: string) => {
        userCollaboratorsStore.revoke(identity, collab, owner_identity).
        then(() => {
            separateCollaborators(userCollaboratorsStore.items);
        });
    };

    const remove = async (identity: string, collab: string, owner_identity: string) => {
        userCollaboratorsStore.remove(identity, collab, owner_identity).
        then(() => {
            separateCollaborators(userCollaboratorsStore.items);
        });
    };

    const getIdentityCollaborators = (identity: string) => {
        //        console.log("getting identity collaborators", identity);
        return collaboratorsStore.items.filter((c) => {
            //            console.log("checking", c);
            return c.identity === identity;
        });
    };

    const getCollaborators = (collabKey: string) => {
        return collaboratorsStore.getCollaborators(collabKey);
    };

    const fetchListForCollab = (identity: string, collab: string) => {
        return collaboratorsStore.fetchListForCollab(identity, collab);
    };

    const data = {
        myCollaborators: myCollaborators,
        fetchListForCollab: fetchListForCollab,
        getCollaborators: getCollaborators,
        snooze: snooze,
        leave: leave,
        activate: activate,
        revoke: revoke,
        remove: remove,

        fetchIdentityCollaborators: fetchIdentityCollaborators,
        fetchIdentityCollaboratorsByManager: fetchIdentityCollaboratorsByManager,

        getIdentityCollaborators: getIdentityCollaborators,

        activeCollaborators: activeCollaborators,
        snoozedCollaborators: snoozedCollaborators,
        archivedCollaborators: archivedCollaborators,
        revokedCollaborators: revokedCollaborators,
        removedCollaborators: removedCollaborators,

    };

    return (
        <CollaboratorsContext.Provider value={data}>{ children }</CollaboratorsContext.Provider>
    );

});
